import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Loader2, Settings2, X, AlertCircle } from 'lucide-react';

export default function SepaCustomizeModal({ plan, onClose, onSubmit, busy }) {
  const initNb = (plan.installments || []).length || 3;
  const initOffsetsArr = (plan.installments || []).map((i, idx) => 30 * (idx + 1));
  const initAmountsArr = (plan.installments || []).map((i) => (i.amount_cents / 100).toFixed(2));

  const buildLines = (nb, baseOffsets, totalEur, customAmounts) => {
    const result = [];
    const baseCents = Math.floor((totalEur * 100) / nb);
    const restCents = Math.round(totalEur * 100) - baseCents * nb;
    for (let i = 0; i < nb; i++) {
      const offset = baseOffsets && baseOffsets[i] !== undefined ? baseOffsets[i] : (i + 1) * 30;
      const amount = customAmounts && customAmounts[i] !== undefined
        ? customAmounts[i]
        : ((baseCents + (i === 0 ? restCents : 0)) / 100).toFixed(2);
      result.push({ offset: String(offset), amount: String(amount) });
    }
    return result;
  };

  const [values, setValues] = useState({
    nb_echeances: String(initNb),
    reference: plan.reference || `RCDS-${plan.souscription_id}`,
    amounts_auto: false,
  });
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const totalEUR = (plan.montant_total_cents || 0) / 100;
  const [lines, setLines] = useState(() => buildLines(initNb, initOffsetsArr, totalEUR, initAmountsArr));

  const nb = Number(values.nb_echeances) || 0;

  useEffect(() => {
    if (values.amounts_auto) {
      setLines((prev) => buildLines(nb, prev.map((l) => Number(l.offset) || 30), totalEUR, null));
    } else if (nb !== lines.length) {
      setLines((prev) => buildLines(nb, prev.map((l) => Number(l.offset) || 30), totalEUR, prev.map((l) => l.amount)));
    }
  }, [nb, totalEUR, values.amounts_auto]); // eslint-disable-line

  const updateLine = (idx, key, val) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  };

  const applyPreset = (offsets) => {
    setLines((prev) => prev.map((l, i) => ({
      ...l,
      offset: String(offsets[i] !== undefined ? offsets[i] : (i + 1) * 30),
    })));
  };

  const today = new Date();
  const fmtDateFR = (offsetDays) => {
    const dt = new Date(today);
    dt.setDate(dt.getDate() + Number(offsetDays || 0));
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const offsetsParsed = lines.map((l) => Number(l.offset)).filter(Number.isFinite);
  const amountsParsed = lines.map((l) => Number(l.amount)).filter(Number.isFinite);
  const sumAmounts = amountsParsed.reduce((a, b) => a + b, 0);
  const allOffsetsOk = offsetsParsed.length === nb && offsetsParsed.every((x) => x > 0);
  const allAmountsOk = amountsParsed.length === nb && amountsParsed.every((x) => x > 0);
  const amountMismatch = !values.amounts_auto && allAmountsOk && Math.abs(sumAmounts - totalEUR) > 0.01;
  const valid = nb >= 1 && nb <= 12 && allOffsetsOk && allAmountsOk && !amountMismatch;

  const handleSubmit = () => {
    const payload = {
      nb_echeances: nb,
      offsets_days: offsetsParsed.join(','),
      reference: (values.reference || '').trim(),
    };
    if (!values.amounts_auto && allAmountsOk && !amountMismatch) {
      payload.amounts_eur = amountsParsed.join(',');
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sepa-customize-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] p-5 flex items-center justify-between text-white">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#f5cc3e]">Super Admin · Plan SEPA</div>
            <h3 className="text-xl font-bold mt-1">Personnaliser l'échéancier</h3>
            <p className="text-xs text-white/70 mt-1">Souscription : <span className="font-mono">{plan.souscription_id}</span> · Total : <span className="font-bold tabular-nums text-[#f5cc3e]">{totalEUR.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="sepa-customize-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Nombre d'échéances (1 à 12)</label>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => set('nb_echeances', String(Math.max(1, nb - 1)))}
                className="h-9 w-9 rounded-lg border-2 border-slate-300 text-slate-600 hover:bg-slate-100 font-bold"
              >−</button>
              <input
                type="number"
                min={1}
                max={12}
                value={values.nb_echeances}
                onChange={(e) => set('nb_echeances', e.target.value)}
                className="flex-1 px-3 py-2 border-2 border-slate-300 rounded-lg text-center font-bold text-lg focus:ring-2 focus:ring-[#DEA900] outline-none"
                data-testid="sepa-customize-nb"
              />
              <button
                type="button"
                onClick={() => set('nb_echeances', String(Math.min(12, nb + 1)))}
                className="h-9 w-9 rounded-lg border-2 border-slate-300 text-slate-600 hover:bg-slate-100 font-bold"
              >+</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase text-slate-500 font-semibold mr-1 self-center">Presets :</span>
              {[
                { label: '3 mens. (J+30/60/90)', offsets: [30, 60, 90], nb: 3 },
                { label: '4 mens. (J+30/60/90/120)', offsets: [30, 60, 90, 120], nb: 4 },
                { label: '6 mens. mensuelles', offsets: [30, 60, 90, 120, 150, 180], nb: 6 },
                { label: '12 mens. mensuelles', offsets: Array.from({ length: 12 }, (_, i) => (i + 1) * 30), nb: 12 },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    set('nb_echeances', String(p.nb));
                    setTimeout(() => applyPreset(p.offsets), 0);
                  }}
                  className="px-2 py-1 text-[10px] rounded-md border border-slate-300 text-slate-700 hover:bg-[#DEA900] hover:text-[#0a3d62] hover:border-[#DEA900] transition-colors font-semibold"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Détail des échéances</label>
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.amounts_auto}
                  onChange={(e) => set('amounts_auto', e.target.checked)}
                  className="rounded border-slate-300"
                />
                Répartition automatique des montants
              </label>
            </div>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-2 text-left w-12">N°</th>
                    <th className="px-2 py-2 text-left">Offset (jours après mandat)</th>
                    <th className="px-2 py-2 text-left">Date prévisionnelle</th>
                    <th className="px-2 py-2 text-right">Montant €</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lines.map((line, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-2 py-2 font-bold text-[#0a3d62]">#{idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-400">J +</span>
                          <input
                            type="number"
                            min={1}
                            max={730}
                            value={line.offset}
                            onChange={(e) => updateLine(idx, 'offset', e.target.value)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded font-mono text-sm focus:ring-2 focus:ring-[#DEA900] outline-none"
                          />
                          <span className="text-[10px] text-slate-400">jours</span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-slate-700 font-semibold whitespace-nowrap">
                        {fmtDateFR(line.offset)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="relative inline-block">
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={line.amount}
                            onChange={(e) => {
                              updateLine(idx, 'amount', e.target.value);
                              if (values.amounts_auto) set('amounts_auto', false);
                            }}
                            disabled={values.amounts_auto}
                            className={`w-28 pl-2 pr-6 py-1 border rounded font-mono text-sm text-right focus:ring-2 focus:ring-[#DEA900] outline-none ${values.amounts_auto ? 'bg-slate-100 border-slate-200 text-slate-500' : 'border-slate-300'}`}
                          />
                          <span className="absolute right-2 top-1 text-slate-400 text-xs">€</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={3} className="px-2 py-2 text-right text-[11px] font-bold text-slate-700 uppercase">Somme :</td>
                    <td className={`px-2 py-2 text-right font-bold tabular-nums ${amountMismatch ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {sumAmounts.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="px-2 py-1 text-right text-[10px] text-slate-500 uppercase">Doit égaler :</td>
                    <td className="px-2 py-1 text-right text-[11px] tabular-nums font-semibold text-slate-700">
                      {totalEUR.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {amountMismatch && (
              <p className="text-[11px] text-rose-700 font-semibold mt-1">
                ⚠️ La somme des montants ({sumAmounts.toFixed(2)} €) ne correspond pas au total ({totalEUR.toFixed(2)} €).
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Référence affichée</label>
            <input
              type="text"
              value={values.reference}
              onChange={(e) => set('reference', e.target.value)}
              maxLength={140}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-[#DEA900] focus:border-[#DEA900] outline-none"
              data-testid="sepa-customize-reference"
            />
          </div>

          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            <AlertCircle className="inline h-4 w-4 mr-1.5" />
            La personnalisation n'est possible <strong>qu'avant signature du mandat</strong>. Si des prélèvements SEPA Stripe existent déjà, annulez-les d&apos;abord.
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="sepa-customize-cancel">Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || !valid}
            className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-bold"
            data-testid="sepa-customize-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
            Enregistrer l'échéancier
          </Button>
        </div>
      </div>
    </div>
  );
}
